import { useCallback, useEffect, useRef, useState } from 'react';

import { MytaverseLogger } from '@helpers/logger';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { LeaveEventReason } from '@providers/MytaverseProvider/interfaces';

import { StreamingProviders } from '../interfaces';

export const BILLING_HEARTBEAT_INTERVAL_MS = 10000;

export enum BillingCloseReason {
  LeftEvent = 'LEFT_EVENT',
  Crash = 'CRASH',
  StreamTimeout = 'STREAM_TIMEOUT',
  UnknownFailure = 'UNKNOWN_FAILURE',
  // Given back on purpose after an hour idle — billed in full, absent from CUT_CLOSE_REASONS below.
  Afk = 'AFK',
}

// Listed positively so a reason added later is not claimable by default.
const CUT_CLOSE_REASONS: BillingCloseReason[] = [
  BillingCloseReason.Crash,
  BillingCloseReason.StreamTimeout,
  BillingCloseReason.UnknownFailure,
];

// An allow-list, so a provider is metered only once someone wires its own
// on-screen signal and its own cut reason. CoreWeave has neither.
const METERED_STREAMING_PROVIDERS: StreamingProviders[] = [
  StreamingProviders.GameCast,
  StreamingProviders.MytaStreams,
];

interface IUseBillingInvoiceProps {
  isWorldOnScreen: boolean;
  streamingProvider: StreamingProviders | null;
  streamSessionId: string | null;
  invoiceId: string | null;
  isGamecastCrash: boolean;
  isGamecastTimeout: boolean;
  cutReason: BillingCloseReason | null;
  isWebsocketOpen: boolean;
  sendJSONMessageToWebSocket: (message: unknown) => void;
  getLeaveEventReason: () => LeaveEventReason;
}

/**
 * Meters one visit to one experience, opening and closing it from the tab.
 *
 * The tab owns the invoice because it outlives its sockets.
 *
 * @param props.isWorldOnScreen - Whether the world is on screen; its edges open
 * and close the invoice. GameCast reports `isEventRun`, MYTA_STREAMS the first
 * decoded frame of a held lease.
 * @param props.streamingProvider - Metered providers are allow-listed.
 * @param props.streamSessionId - The stream this visit came up on: GameCast's
 * full ARN, or MYTA_STREAMS' lease id.
 * @param props.invoiceId - Id the server answered an open with.
 * @param props.isGamecastCrash - Latched as the close reason before the cut.
 * @param props.isGamecastTimeout - Latched as the close reason before the cut.
 * @param props.cutReason - Latched the same way by a provider that names its
 * own cut, rather than exposing flags to watch.
 * @param props.isWebsocketOpen - Beats are skipped, never queued, while closed.
 * @param props.sendJSONMessageToWebSocket - Sender for every billing message.
 * @param props.getLeaveEventReason - Read on unmount to attach why the page
 * was left, alongside (not merged into) the fixed `BillingCloseReason`.
 * @returns `handleStale`, to call when the server says an invoice cannot accrue.
 */
export const useBillingInvoice = ({
  isWorldOnScreen,
  streamingProvider,
  streamSessionId,
  invoiceId,
  isGamecastCrash,
  isGamecastTimeout,
  cutReason,
  isWebsocketOpen,
  sendJSONMessageToWebSocket,
  getLeaveEventReason,
}: IUseBillingInvoiceProps) => {
  const hasOpenInvoiceRef = useRef(false);
  const prevIsWorldOnScreenRef = useRef(false);
  const firstBeatSentIdRef = useRef<string | null>(null);
  const closeReasonRef = useRef<BillingCloseReason | null>(null);
  const lastCutInvoiceRef = useRef<string | null>(null);
  const [hasOpenInvoice, setHasOpenInvoice] = useState(false);

  const invoiceIdRef = useRef(invoiceId);
  const streamSessionIdRef = useRef(streamSessionId);
  const sendRef = useRef(sendJSONMessageToWebSocket);
  const isSocketOpenRef = useRef(isWebsocketOpen);

  useEffect(() => {
    invoiceIdRef.current = invoiceId;
  }, [invoiceId]);

  useEffect(() => {
    streamSessionIdRef.current = streamSessionId;
    sendRef.current = sendJSONMessageToWebSocket;
    isSocketOpenRef.current = isWebsocketOpen;
  }, [streamSessionId, sendJSONMessageToWebSocket, isWebsocketOpen]);

  useEffect(() => {
    if (isGamecastCrash) {
      closeReasonRef.current = BillingCloseReason.Crash;
    } else if (isGamecastTimeout) {
      closeReasonRef.current = BillingCloseReason.StreamTimeout;
    }
  }, [isGamecastCrash, isGamecastTimeout]);

  // Kept apart from the GameCast branch above so neither provider can change
  // the other's latch. Cleared by every open, like the flags are.
  useEffect(() => {
    if (cutReason) {
      closeReasonRef.current = cutReason;
    }
  }, [cutReason]);

  const openInvoice = useCallback(() => {
    // A beat still carrying the replaced id would be answered about the dead
    // invoice, and that answer would open a third one.
    invoiceIdRef.current = null;
    firstBeatSentIdRef.current = null;
    // An earlier failure must not become this invoice's end reason.
    closeReasonRef.current = null;

    sendRef.current({
      action: WebsocketAction.BillingOpen,
      streamProvider: streamingProvider,
      streamSessionId: streamSessionIdRef.current ?? undefined,
      heartbeatIntervalMs: BILLING_HEARTBEAT_INTERVAL_MS,
      continuesInvoiceId: lastCutInvoiceRef.current ?? undefined,
    });

    lastCutInvoiceRef.current = null;
    hasOpenInvoiceRef.current = true;
    setHasOpenInvoice(true);
  }, [streamingProvider]);

  const closeInvoice = useCallback(
    (reason: BillingCloseReason, leaveEventReason?: LeaveEventReason) => {
      if (!hasOpenInvoiceRef.current) {
        return;
      }

      hasOpenInvoiceRef.current = false;
      setHasOpenInvoice(false);

      sendRef.current({
        action: WebsocketAction.BillingClose,
        invoiceId: invoiceIdRef.current ?? undefined,
        reason,
        ...(leaveEventReason ? { leaveEventReason } : {}),
      });

      // Kept apart from the beating id, which must not outlive its invoice: a
      // beat carrying a replaced id would be answered and open a third invoice.
      lastCutInvoiceRef.current = CUT_CLOSE_REASONS.includes(reason)
        ? invoiceIdRef.current
        : null;

      invoiceIdRef.current = null;
      firstBeatSentIdRef.current = null;
    },
    [],
  );

  const handleStale = useCallback(
    (staleInvoiceId?: string) => {
      if (!hasOpenInvoiceRef.current) {
        MytaverseLogger.debug(
          `Billing stale answer ignored, no invoice is open: ${
            staleInvoiceId ?? '(none sent)'
          }`,
        );

        return;
      }

      if (staleInvoiceId && staleInvoiceId !== invoiceIdRef.current) {
        MytaverseLogger.debug(
          `Billing stale answer ignored, it names a previous invoice: ${staleInvoiceId}, metering ${invoiceIdRef.current}`,
        );

        return;
      }

      MytaverseLogger.debug(
        `Billing invoice can no longer accrue, metering the rest of the visit under a new one: ${
          staleInvoiceId ?? '(none sent)'
        }`,
      );

      openInvoice();
    },
    [openInvoice],
  );

  useEffect(() => {
    if (
      !streamingProvider ||
      !METERED_STREAMING_PROVIDERS.includes(streamingProvider)
    ) {
      return;
    }

    if (isWorldOnScreen && !prevIsWorldOnScreenRef.current) {
      prevIsWorldOnScreenRef.current = true;
      openInvoice();

      return;
    }

    if (!isWorldOnScreen && prevIsWorldOnScreenRef.current) {
      prevIsWorldOnScreenRef.current = false;
      closeInvoice(closeReasonRef.current ?? BillingCloseReason.UnknownFailure);
    }
  }, [isWorldOnScreen, streamingProvider, openInvoice, closeInvoice]);

  // The interval's first tick is a period away, so a shorter visit would take
  // no beat and be valued at nothing. Beaten off the ref, not the prop: the
  // prop is never cleared and still names the previous invoice after a
  // restart. Socket check as in the interval: a queued beat replays at arrival.
  useEffect(() => {
    if (
      !invoiceId ||
      !hasOpenInvoice ||
      !isSocketOpenRef.current ||
      invoiceIdRef.current !== invoiceId ||
      firstBeatSentIdRef.current === invoiceId
    ) {
      return;
    }

    firstBeatSentIdRef.current = invoiceId;

    sendRef.current({
      action: WebsocketAction.BillingBeat,
      invoiceId,
    });
  }, [invoiceId, hasOpenInvoice]);

  useEffect(() => {
    if (!hasOpenInvoice) {
      return;
    }

    const interval = setInterval(() => {
      // Skipped, not queued: the sender replays on reconnect and the server
      // stamps arrival, so a replay claims presence after the visitor left.
      if (!isSocketOpenRef.current) {
        return;
      }

      sendRef.current({
        action: WebsocketAction.BillingBeat,
        invoiceId: invoiceIdRef.current ?? undefined,
      });
    }, BILLING_HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasOpenInvoice]);

  useEffect(() => {
    return () => {
      closeInvoice(BillingCloseReason.LeftEvent, getLeaveEventReason());
    };
  }, [closeInvoice, getLeaveEventReason]);

  return { handleStale };
};
